// ============================================
// CALENDAR FILTER HELPER
// ============================================
//
// t16: applies a saved view's `filters` payload to the task list before
// rendering. Mirrors the kanban-status-config.ts predicate so a task
// hidden in Kanban is also hidden in Calendar for the same view.
//
// The shape is `FilterRule[]` (the same shape the Kanban columns use),
// not the legacy `filterConfig` blob. We accept either form so a
// brand-new Calendar seed (which only has `filters: {}`) renders
// everything without an explicit `FilterRule[]`.

import type {
    CustomField,
    FilterRule,
    Task,
    ViewConfig,
} from '@/lib/schemas';

interface NormalisedFilterPayload {
    search?: string;
    rules: FilterRule[];
    // t43 (R13): accepts the new per-type filter shape OR the
    // legacy option-id-list. The Calendar's local matcher only
    // knows about the legacy anyOf semantics, so unknown shapes
    // are ignored for now — backend applies the per-type filters
    // at query time, and the Calendar's client-side matcher is a
    // safety net.
    customFields?: Record<string, unknown>;
}

function normaliseFilterPayload(filters: ViewConfig['filters'] | undefined): NormalisedFilterPayload {
    if (!filters) return { rules: [] };
    const rules: FilterRule[] = [];
    const pushRule = (field: string, operator: FilterRule['operator'], value?: string | string[]) => {
        rules.push(value === undefined
            ? { field, operator }
            : { field, operator, value });
    };
    if (Array.isArray(filters.status) && filters.status.length > 0) {
        pushRule('status', 'in', filters.status as string[]);
    }
    if (Array.isArray(filters.priority) && filters.priority.length > 0) {
        pushRule('priority', 'in', filters.priority as string[]);
    }
    if (Array.isArray(filters.assignees) && filters.assignees.length > 0) {
        pushRule('assignee.id', 'in', filters.assignees as string[]);
    }
    return {
        search: filters.search,
        rules,
        customFields: filters.customFields,
    };
}

// Decide whether `task` matches a single FilterRule. Mirrors
// matchesRule() in kanban-status-config.ts so the two views behave
// identically for the same predicate.
function matchesRule(task: Task, rule: FilterRule, customFields: CustomField[]): boolean {
    const { field, operator } = rule;
    let actual: unknown;
    if (field === 'status') actual = task.status;
    else if (field === 'priority') actual = task.priority;
    else if (field === 'assignee.id' || field === 'assignee') {
        actual = task.assignees.map((a) => a.userId);
    } else if (field.startsWith('customField.')) {
        const cfId = field.slice('customField.'.length);
        actual = task.customFieldValues?.[cfId];
    } else {
        // Bare id → custom-field.
        actual = task.customFieldValues?.[field];
    }

    switch (operator) {
        case 'isEmpty':
            return actual == null || actual === '' || (Array.isArray(actual) && actual.length === 0);
        case 'isNotEmpty':
            return !(actual == null || actual === '' || (Array.isArray(actual) && actual.length === 0));
        case 'eq': {
            if (Array.isArray(actual)) return actual.includes(String(rule.value));
            return String(actual ?? '') === String(rule.value ?? '');
        }
        case 'neq': {
            if (Array.isArray(actual)) return !actual.includes(String(rule.value));
            return String(actual ?? '') !== String(rule.value ?? '');
        }
        case 'in': {
            const allowed = Array.isArray(rule.value) ? rule.value : [String(rule.value ?? '')];
            if (Array.isArray(actual)) return actual.some((v) => allowed.includes(String(v)));
            return allowed.includes(String(actual ?? ''));
        }
        case 'notIn': {
            const blocked = Array.isArray(rule.value) ? rule.value : [String(rule.value ?? '')];
            if (Array.isArray(actual)) return !actual.some((v) => blocked.includes(String(v)));
            return !blocked.includes(String(actual ?? ''));
        }
        case 'contains': {
            const needle = String(rule.value ?? '').toLowerCase();
            if (Array.isArray(actual)) return actual.some((v) => String(v).toLowerCase().includes(needle));
            return String(actual ?? '').toLowerCase().includes(needle);
        }
    }
    return false;
}

// Apply the saved view's filters to a task list. Search is a
// case-insensitive substring match on title; rules are AND-combined;
// customFields are an AND-combined option-id match per field.
export function applyCalendarFilters(
    tasks: Task[],
    filters: ViewConfig['filters'] | undefined,
    customFields: CustomField[]
): Task[] {
    const payload = normaliseFilterPayload(filters);
    const search = (payload.search ?? '').trim().toLowerCase();

    return tasks.filter((task) => {
        if (search) {
            const inTitle = task.title?.toLowerCase().includes(search);
            if (!inTitle) return false;
        }

        // Legacy customFields filter (field id → selected option ids).
        // t43 (R13): per-type filters are evaluated server-side at
        // query time. The legacy shape is still accepted so pre-R13
        // saved views keep working client-side as a safety net.
        if (payload.customFields) {
            for (const [fieldId, raw] of Object.entries(payload.customFields)) {
                const optionIds = Array.isArray(raw)
                    ? raw
                    : null;
                if (!optionIds || optionIds.length === 0) continue;
                const taskRaw = task.customFieldValues?.[fieldId];
                const present = Array.isArray(taskRaw)
                    ? taskRaw
                    : taskRaw == null
                    ? []
                    : [String(taskRaw)];
                const anyMatch = optionIds.some((id) => present.includes(id));
                if (!anyMatch) return false;
            }
        }

        // FilterRule[] (the new shape).
        for (const rule of payload.rules) {
            if (!matchesRule(task, rule, customFields)) return false;
        }

        return true;
    });
}
